// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// The two frames an avatar's pose passes through (M8-02, ADR 0003).
//
// WHICH FRAME A POSE IS IN, AND WHY IT IS NOT THE OBVIOUS ONE
// ----------------------------------------------------------
// Two documents disagreed and the disagreement had teeth. 10 §5 says `pose-v1` carries "head and
// wrist poses, venue-local frame", and calls `hcc-pinball-venue-local-rh-v1` the frame "used for
// avatar poses, so two players in different real rooms stand in the same virtual arcade". 06 §4
// says a Quest player is "pose-streamed at 15 Hz with receiver-side interpolation, in the
// **playfield-local frame** so alignment survives different real rooms (the direct analogue of
// Chess ADR 0003)".
//
// **06 §4 is the one that survives contact with mixed reality, so it is the one implemented.** In
// native MR there is no venue: there is one cabinet standing in somebody's living room, at a
// position and yaw they chose by hand and may change mid-game by picking it up. A venue-local pose
// would place a remote player three metres to the left of an arcade that does not exist — in the
// kitchen, through a wall. A playfield-local pose is relative to *the machine*, so it lands
// correctly against whatever cabinet the receiver has, wherever that cabinet happens to be.
//
// **Both frames survive; they just have different jobs.** `hcc-pinball-venue-local-rh-v1` says
// where the six bays are — the arcade's own layout, identical for every client that renders one.
// `hcc-pinball-playfield-local-rh-v1` says where a person is at one. An avatar's world position is
// the composition of the two, and in MR the first term is simply absent.
//
// So the wire carries a **bay index and a playfield-local pose**, and this file is the arithmetic
// that turns one into the other in both directions. It has no Three.js, no Unity and no network in
// it, because the C# client has to agree with it exactly and the only way to hold two
// implementations of a rotation identical is to test them both against the same fixtures.

/** A point in metres. */
export interface Vec3 {
  readonly x: number
  readonly y: number
  readonly z: number
}

/**
 * Where a bay's machine stands in the venue.
 *
 * Position and yaw only. A machine is never pitched or rolled in a venue — a tilted cabinet is a
 * cabinet somebody knocked over — so the transform is a rigid rotation about Y plus a translation,
 * which is exactly six numbers and inverts without a matrix library.
 */
export interface VenuePlacement {
  readonly x: number
  readonly y: number
  readonly z: number
  /** Radians. The convention is `Placement`'s: front at yaw θ is (−sin θ, cos θ). */
  readonly yaw: number
}

/**
 * The frame names, as they appear on the wire.
 *
 * Versioned strings rather than an enum, because a frame is a contract: a client that receives a
 * name it does not know must be able to say so rather than guess, and a name that changes shape is
 * a new name.
 */
export const PLAYFIELD_FRAME = 'hcc-pinball-playfield-local-rh-v1'
export const VENUE_FRAME = 'hcc-pinball-venue-local-rh-v1'

/**
 * A playfield-local point, in the venue's frame, given where that machine stands.
 *
 * The rotation is about Y only, and is written out rather than composed from a library because
 * this exact arithmetic exists twice — here and in `App/Xr/VenueFrame.cs` — and the two are held
 * identical by fixtures. A matrix library on one side and hand-written trigonometry on the other
 * is how two clients end up disagreeing by a sign nobody can find.
 *
 * **This function and `toPlayfield` shipped with each other's bodies, and every test passed.**
 * They are exact inverses, so round-tripping worked; a rotation the wrong way preserves distance
 * and handedness, so those checks passed too; and every bay in the arcade has yaw 0, where the two
 * directions agree — so it could only ever have shown up in MR, at a cabinet the player had turned
 * by hand, which is the one case with no test and no second opinion. What catches it now is
 * `toRoomPoint` in `src/xr/placement.ts`: the same transform, already correct, already shipping the
 * machine itself. One convention with two implementations must be cross-checked against each other
 * and not merely against itself.
 */
export function toVenue(at: Vec3, machine: VenuePlacement): Vec3 {
  const s = Math.sin(machine.yaw)
  const c = Math.cos(machine.yaw)
  return {
    x: machine.x + at.x * c - at.z * s,
    y: machine.y + at.y,
    z: machine.z + at.x * s + at.z * c,
  }
}

/** And back again. The exact inverse, which the fixtures check by round-tripping. */
export function toPlayfield(at: Vec3, machine: VenuePlacement): Vec3 {
  const s = Math.sin(machine.yaw)
  const c = Math.cos(machine.yaw)
  const dx = at.x - machine.x
  const dy = at.y - machine.y
  const dz = at.z - machine.z
  return {
    x: dx * c + dz * s,
    y: dy,
    z: -dx * s + dz * c,
  }
}

/**
 * A yaw expressed against a machine, expressed against the venue.
 *
 * Kept separate from the point conversion because a pose is a position *and* a facing, and the
 * facing is not derivable from the position. Wrapped, so a receiver never interpolates the long
 * way round a circle it did not know it had crossed.
 */
export function yawToVenue(yaw: number, machine: VenuePlacement): number {
  return wrap(yaw + machine.yaw)
}

/** And back. */
export function yawToPlayfield(yaw: number, machine: VenuePlacement): number {
  return wrap(yaw - machine.yaw)
}

/** Wrap an angle into −π … π. */
export function wrap(a: number): number {
  let r = a
  while (r > Math.PI) r -= 2 * Math.PI
  while (r <= -Math.PI) r += 2 * Math.PI
  return r
}
